import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { listFiltersService, ListTableSort, getSortedCollection, ensure } from "sprint-common-web";

import { TvSeasonDetails, EpisodeDetails, TvSeriesDetails } from "../../../../TvSeriesDetails";
import { TvSeriesContext } from "../../../context/TvSeriesContext";
import { SeasonPageContext } from "../../context/SeasonPageContext";
import { EpisodeListingFilters, defaultEpisodeListingFilters } from "../EpisodeListingFilters";
import { EpisodeListingItem, mapToEpisodeListingItem } from "../EpisodeListingItem";
import { SeasonEpisodeListingContext } from "./SeasonEpisodeListingContext";

type SeasonEpisodeListingProviderProps = React.PropsWithChildren<unknown>;

export const SeasonEpisodeListingProvider: FunctionComponent<SeasonEpisodeListingProviderProps> = (
    props: SeasonEpisodeListingProviderProps
) => {
    const { children } = props;

    const tvSeriesDetailsContext = useContext(TvSeriesContext);
    const { selectedSeason, addEpisode: addEpisodeInParentCtx } = useContext(SeasonPageContext);
    const [episodesToMerge, setEpisodesToMerge] = useState<EpisodeListingItem[]>([]);
    const [listingItems, setListingItems] = useState<EpisodeListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);

    const isLoading = tvSeriesDetailsContext.isLoading;
    const filtersService = useMemo(() => listFiltersService<EpisodeListingFilters>(defaultEpisodeListingFilters), []);
    const location = useLocation();
    const history = useHistory();
    const [filters, setFilters] = useState<EpisodeListingFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    ensure(tvSeriesDetailsContext.tvSeries);

    useEffect(() => {
        if (!isLoading && filters) {
            setFilteredAndSortedLisitngItems(filters);
        }
    }, [filters, isLoading]);

    const addNewEpisode = (newEpisode: EpisodeDetails) => {
        const newListingItems = getListWithNewItemOnTop(listingItems, filters, newEpisode);
        setListingItems(newListingItems);

        addEpisodeInParentCtx(newEpisode);
    };

    const setSort = (sort: ListTableSort) => {
        saveAndSetFilters({
            sortBy: sort.by as keyof EpisodeListingItem,
            sortOrder: sort.order,
            page: 1
        });
    };

    const setPage = (page: number) => {
        saveAndSetFilters({ page });
    };

    const setPageSize = (pageSize: number): void => {
        saveAndSetFilters({ pageSize, page: 1 });
    };

    const setOrphanedOnly = (orphanedOnly?: boolean): void => {
        saveAndSetFilters({ orphanedOnly });
    };

    const setIncludeMerged = (includeMerged?: boolean): void => {
        saveAndSetFilters({ includeMerged });
    };

    const saveAndSetFilters = (newFilters: Partial<EpisodeListingFilters>): void => {
        setFilters((prevFilters: EpisodeListingFilters) => {
            const updatedFilters = { ...prevFilters, ...newFilters };

            filtersService.saveFilters(location.pathname, history, updatedFilters);

            return updatedFilters;
        });
    };

    const clearFilters = (): void => {
        saveAndSetFilters(defaultEpisodeListingFilters);
    };

    const setFilteredAndSortedLisitngItems = (filter: EpisodeListingFilters, tvSeason?: TvSeasonDetails) => {
        const episodes = selectedSeason.episodes;

        const filteredItems = getFilteredEpisodeItems(mapToEpisodeItems(episodes), filter);
        setTotalItems(filteredItems.length);

        const sortedItems = getSortedCollection(filteredItems, filter.sortBy, filter.sortOrder);
        const currentPageItems = getCurrentPageItems(sortedItems, filter);

        setListingItems(currentPageItems);
    };

    const getCurrentPageItems = (items: EpisodeListingItem[], filter: EpisodeListingFilters): EpisodeListingItem[] => {
        const firstItem = (filter.page - 1) * filter.pageSize;
        const lastItem = filter.page * filter.pageSize;

        return items.slice(firstItem, lastItem);
    };

    const getFilteredEpisodeItems = (
        items: EpisodeListingItem[],
        filter: EpisodeListingFilters
    ): EpisodeListingItem[] => {
        let filteredEpisodes = items;

        filteredEpisodes = filter.includeMerged
            ? filteredEpisodes
            : filteredEpisodes.filter((version: EpisodeListingItem) => !version.merged === !filter.includeMerged);

        filteredEpisodes = filter.orphanedOnly
            ? filteredEpisodes.filter((episode: EpisodeListingItem) => episode.orphaned)
            : filteredEpisodes;

        return filteredEpisodes;
    };

    const removeEpisodeFromMergeList = (episodeToRemove: EpisodeListingItem) => {
        setEpisodesToMerge((prevEpisode: EpisodeListingItem[]) => {
            return prevEpisode.filter((episode: EpisodeListingItem) => episodeToRemove.id !== episode.id);
        });
    };

    const updateListAfterMerge = async (newEpisodeId: string): Promise<void> => {
        const updateEpisodeListAfterMerge = (tvSeries: TvSeriesDetails): void => {
            const actualSeason = selectedSeason;

            setFilteredAndSortedLisitngItems(filters, actualSeason);

            setListingItems((prevItems: EpisodeListingItem[]) => {
                const newEpisode = actualSeason.episodes.find((episode: EpisodeDetails) => episode.id === newEpisodeId);
                if (!newEpisode) return prevItems;

                newEpisode.isModified = true;
                return getListWithNewItemOnTop(prevItems, filters, newEpisode);
            });
        };

        await tvSeriesDetailsContext.silentTvSeriesRefresh(updateEpisodeListAfterMerge);
    };

    const addEpisodeToMergeList = (item: EpisodeListingItem) => {
        setEpisodesToMerge((prevEpisodeItems: EpisodeListingItem[]) => {
            return [...prevEpisodeItems, item];
        });
    };

    const clearEpisodesToMerge = () => {
        setEpisodesToMerge([]);
    };

    return (
        <SeasonEpisodeListingContext.Provider
            value={{
                episodesToMerge,
                listingItems,
                totalItems,
                selectedSeason,
                setIncludeMerged,
                clearFilters,
                setSort,
                setPage,
                setPageSize,
                setOrphanedOnly,
                addNewEpisode,
                clearEpisodesToMerge,
                updateListAfterMerge,
                removeEpisodeFromMergeList,
                addEpisodeToMergeList,
                filters,
                selectedTvSeries: tvSeriesDetailsContext.tvSeries
            }}
        >
            {children}
        </SeasonEpisodeListingContext.Provider>
    );
};

const mapToEpisodeItems = (episodes: EpisodeDetails[], resetNewFlag = true): EpisodeListingItem[] => {
    return episodes.map((episode: EpisodeDetails) =>
        mapToEpisodeListingItem(episode, resetNewFlag ? false : episode.isModified)
    );
};

const getListWithNewItemOnTop = (
    itemList: EpisodeListingItem[],
    filters: EpisodeListingFilters,
    newEpisode: EpisodeDetails
) => {
    if (itemList.length >= filters.pageSize) itemList.pop();
    const filteredPrevItems = itemList.filter((item: EpisodeListingItem) => item.id !== newEpisode.id);
    return [mapToEpisodeListingItem(newEpisode, newEpisode.isModified), ...filteredPrevItems];
};
